import { useMemo } from "react";
import { useCurrentUser } from "./useCurrentUser";
import { agentRoles } from "../constants/agent";

const extractRoleNumbers = (raw: unknown): number[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((r: any) => (typeof r === "object" && r !== null ? r.role : r))
    .filter((r: any) => typeof r === "number");
};

/**
 * Returns the effective role set for the current user — the union of:
 *   1. job_roles[] from the new agent_role_assignment junction
 *   2. agent_role permission level (legacy field), so company_admin / super_admin
 *      still satisfy admin-only sidebar filters even if their junction is empty.
 *
 * Sidebar/permission filters should match against this set.
 */
export const useEffectiveRoles = (): Set<number> => {
  const user = useCurrentUser() as any;

  return useMemo(() => {
    const set = new Set<number>(extractRoleNumbers(user?.job_roles));
    const legacy = user?.agent_role;
    if (
      legacy === agentRoles.company_admin ||
      legacy === agentRoles.super_admin
    ) {
      set.add(legacy);
    }
    // Manager == company admin. Anything gated on company_admin (sidebar
    // items, approve buttons, settings tabs) is open to Managers as well.
    // Super admins keep only their own level so company-scoped items that
    // need a belongs_to_company_id stay hidden for them.
    if (
      legacy !== agentRoles.super_admin &&
      set.has(agentRoles.management)
    ) {
      set.add(agentRoles.company_admin);
    }
    if (legacy === agentRoles.agent && set.size === 0) {
      // Old account with no junction yet (pre-migration JWT). Treat as
      // backoffice (matches migration default) so they can still see tickets
      // until an admin reassigns them.
      set.add(agentRoles.backoffice);
    }
    return set;
  }, [user?.job_roles, user?.agent_role]);
};

export const hasAnyRole = (
  roles: Set<number>,
  required: number[]
): boolean => required.some((r) => roles.has(r));
